import * as RadixAccordion from '@radix-ui/react-accordion';

import MinusIcon from '@cointracker/styleguide/src/icons/minus-icon.svg?react';
import PlusIcon from '@cointracker/styleguide/src/icons/plus-icon.svg?react';
import classNames from 'clsx';
import { useState, type ReactNode } from 'react';
import { Button } from '../Button';
import { DividerLine } from '../DividerLine';
import { Heading } from '../LoggedIn';
import { Body2 } from '../typography';
import {
  styledAccordionContainer,
  styledAccordionContentWrapper,
  styledAccordionHeading,
  styledAccordionItem,
  styledAccordionLink,
  styledAccordionLinkContainer,
  styledAccordionRoot,
  styledAccordionTrigger,
  styledAccordionTriggerContent,
  styledAccordionTriggerHeading,
  styledIcon,
  styledIconContainer,
} from './AccordionModule.css';

//documentation https://www.radix-ui.com/primitives/docs/components/accordion
interface RootProps {
  children: ReactNode;
  heading: ReactNode;
  type?: 'multiple' | 'single';
}

export const Root = ({ children, heading, type = 'multiple' }: RootProps) => {
  return (
    <RadixAccordion.Root type={type} className={styledAccordionRoot}>
      <div className={classNames(styledAccordionHeading)}>{heading}</div>
      <div className={classNames(styledAccordionContainer)}>{children}</div>
    </RadixAccordion.Root>
  );
};

interface AccordionItemProps extends RadixAccordion.AccordionItemProps {
  headline: string;
  headingAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  link?: {
    title: string;
    href: string;
  };
}

export const Item = (props: AccordionItemProps) => {
  const { children, headline, headingAs, link, ...rest } = props;

  return (
    <RadixAccordion.Item
      {...rest}
      className={classNames(props.className, styledAccordionItem)}
    >
      <Trigger headline={headline} headingAs={headingAs} />
      <Content link={link}>{children}</Content>
    </RadixAccordion.Item>
  );
};

interface ContentProps extends RadixAccordion.AccordionContentProps {
  children: ReactNode;
  link?: {
    title: string;
    href: string;
  };
}

export const Content = (props: ContentProps) => {
  const { children, link, ...rest } = props;

  return (
    <RadixAccordion.Content
      {...rest}
      className={classNames(styledAccordionContentWrapper)}
    >
      <div>{children}</div>

      {link?.title && link?.href && (
        <div>
          <Button
            variant="textLink"
            hideInitialIcon={true}
            href={link.href}
            className={styledAccordionLink}
          >
            {link.title}
          </Button>
        </div>
      )}
    </RadixAccordion.Content>
  );
};

interface TriggerProps extends RadixAccordion.AccordionTriggerProps {
  headline: string;
  headingAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const Trigger = (props: TriggerProps) => {
  const { headline, headingAs, ...rest } = props;

  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <RadixAccordion.Trigger
      {...rest}
      className={styledAccordionTrigger}
      onClick={handleToggle}
    >
      <DividerLine />
      <div className={styledAccordionTriggerContent}>
        <Heading
          variant="h6"
          as={headingAs ?? 'h6'}
          className={styledAccordionTriggerHeading}
        >
          {headline}
        </Heading>
        <div className={styledIconContainer}>
          {isOpen ? (
            <MinusIcon className={styledIcon} />
          ) : (
            <PlusIcon className={styledIcon} />
          )}
        </div>
      </div>
    </RadixAccordion.Trigger>
  );
};

interface AccordionLinkContainer {
  children: ReactNode;
}

export const AccordionLinkContainer = (props: AccordionLinkContainer) => {
  const { children, ...rest } = props;
  return (
    <div {...rest} className={styledAccordionLinkContainer}>
      {children}
    </div>
  );
};

interface AccordionLink {
  children: ReactNode;
  href: string;
}

export const AccordionLink = (props: AccordionLink) => {
  const { children, href, ...rest } = props;
  return (
    <a href={href} {...rest} className={styledAccordionLink}>
      <Body2>{children}</Body2>
    </a>
  );
};
